import {
  Component, OnInit, ViewEncapsulation, SimpleChanges, OnChanges, Input, ElementRef, ViewChildren, QueryList, NgZone, Renderer2
} from '@angular/core';

import { Vraag, Antwoord, ValidatieParameter } from '@models/claims';
import { UploadEvent, Devicetype, FileSystemFileEntry, Proces } from '../../../models/imports/generic';
import { Verzekering, Meetgegevens, VraagboomUploadResultaat, Uploadfile } from '@models/vragenboom';
import { BaseVraagControlDirective } from '../base/base-vraag-control';
import { ImageHelper } from '../../../utils/helpers/image-helper';
import { ValidatieregelHelper } from '@services/public_api';
import { MetenMapper } from '../../../mappers/meten.mapper';
import { DataService } from '../../../services/data.service';
import { WindowService } from '../../../services/window-service';
import { FileValidator } from '../../../utils/validation/file.validator';
import { ImageService } from '../../../services/image.service';
import { AnalyticsService } from '@crosscutting/public_api';

import { EMPTY, Observable, of } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-multiupload',
  templateUrl: './multiupload.component.html',
  styleUrls: ['./multiupload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MultiUploadComponent extends BaseVraagControlDirective<VraagboomUploadResultaat> implements OnInit, OnChanges {
  @ViewChildren('newFile') newFiles: QueryList<ElementRef>;

  minChoices = 0;
  maxChoices = 0;
  groepen: Antwoord[];
  selectedIds: string[] = [];
  _verzekering: Verzekering;
  _meetgegevens: Meetgegevens;

  maxSizeInMb = 1;
  maxTotalSizeInMb: number;
  totalSizeInBytes: number;
  resultaat: VraagboomUploadResultaat = new VraagboomUploadResultaat();
  numberOfUploadingFiles = 0;
  indexFileToDelete = -1;
  deleteFilename: string;
  antwoordFileToDelete: Antwoord;
  errorGroepId: string;
  isHovered = false;

  @Input() isCurrentVraag: boolean;

  constructor(
    private zone: NgZone,
    private renderer: Renderer2,
    private _analyticsService: AnalyticsService,
    _dataService: DataService,
    private _imageService: ImageService,
    private _windowService: WindowService
  ) {
    super(_dataService);
  }

  ngOnInit() {
    this._verzekering = this._dataService.getVerzekering();
    this._meetgegevens = this._dataService.getMeetgegevens();
  }

  initialize(changes: SimpleChanges): void {
    if (this.antwoordResultaat != null && this.antwoordResultaat.resultaat != null && this.antwoordResultaat.antwoorden != null) {
      this.selectedIds = this.antwoordResultaat.antwoorden.map(a => a.id);
    }

    if (this.vraag) {
      // aantal selectie en maximale filesize zit op vraag
      const minMaxNumberValidator: ValidatieParameter = ValidatieregelHelper.get(this.vraag.validatieParameters, 'MIN_MAX');
      if (minMaxNumberValidator != null) {
        const params = minMaxNumberValidator.validatie_parameters.split(';');
        if (params != null && params.length > 1) {
          this.minChoices = Number(params[0]);
          this.maxChoices = Number(params[1]);
        }
      }

      const maxSizeValidator: ValidatieParameter = ValidatieregelHelper.get(this.vraag.validatieParameters, 'MAX_SIZE');
      if (maxSizeValidator != null) {
        this.maxTotalSizeInMb = Number(maxSizeValidator.validatie_parameters);
      }

      // toegestane extensies en aantal bestanden zit op antwoord
      if (this.vraag.antwoorden !== null && this.vraag.antwoorden.length > 0) {
        let groepId = '';
        this.groepen = [];

        this.vraag.antwoorden.forEach((antw: Antwoord) => {
          if (antw.groep_id !== groepId) {
            // Toegestane extensies
            antw.inputAllowExtensions = '';
            for (const extension of FileValidator.allowedExtensionsAntwoord(antw)) {
              if (ImageHelper.isImage(extension)) {
                antw.inputAllowExtensions = antw.inputAllowExtensions + 'image/' + extension + ',';
              } else {
                antw.inputAllowExtensions = antw.inputAllowExtensions + '.' + extension + ',';
              }
            }
            // Laatste komma verwijderen
            if (antw.inputAllowExtensions.length > 0) {
              antw.inputAllowExtensions = antw.inputAllowExtensions.substring(0, antw.inputAllowExtensions.length - 1);
            }

            antw.errorMessage = [];
            this.groepen.push(antw);
            groepId = antw.groep_id;
          }
        });
      }
    }

    this.totalSizeInBytes = 0;

    if (this.antwoordResultaat?.resultaat != null) {
      this.resultaat = this.antwoordResultaat.resultaat;

      if (this.resultaat.uploadedFiles?.length > 0) {
        this._analyticsService.celebrusReinit();

        for (const uploadedfile of this.resultaat.uploadedFiles) {
          this.totalSizeInBytes = this.totalSizeInBytes + (uploadedfile.blob.length * 3 / 4);
        }
      }
    }
  }

  isFileImage(filename: string) {
    return ImageHelper.isFileImage(filename)
  }

  addErrorMessage(message: string, antw: Antwoord): void {
    if (antw.errorMessage?.indexOf(message) < 0) {
      this.zone.run(() => {
        antw.errorMessage.push(message);
      });
    }
  }

  multiselectClicked(antwoord: Antwoord) {
    if (!this.isCurrentVraag) {
      return;
    }

    if (!this.selectedIds) {
      this.selectedIds = [];
    }

    if (this.selectedIds?.indexOf(antwoord.id) >= 0) {
      this.selectedIds = this.selectedIds.filter(id => id !== antwoord.id);
    } else {
      this.selectedIds.push(antwoord.id);
    }

    this.notifyChange();
  }

  antwoordGeselecteerd(antwoord: Antwoord): boolean {
    if (antwoord && this.selectedIds && this.selectedIds.indexOf(antwoord.id) >= 0) {
      return true;
    }
    return false;
  }

  dropped(event: UploadEvent, antw: Antwoord) {
    this.resetError(antw);
    this.isHovered = false;

    for (const droppedFile of event.files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.uploadFile(file, antw);
        });
      }
    }
  }

  onFileChanged($event, antw: Antwoord): void {
    if ($event.target == null || $event.target.files.length <= 0 || antw == null) {
      return;
    }

    this.resetError(antw);
    for (const file of $event.target.files) {
      this.uploadFile(file, antw);
    }
    this.clearUploadedFile();
  }

  resetError(antw: Antwoord) {
    antw.errorMessage = [];
  }

  geefMaxFiles(antwoord_id: string): number {
    const antwoord = this.vraag.antwoorden.find(a => a.id === antwoord_id);

    if (antwoord) {
      // MIN_MAX op antwoord is niet mogelijk dus aantal antwoorden met dezelfde groep_id tellen
      const antwoordenInGroep = this.vraag.antwoorden.filter(a => a.groep_id === antwoord.groep_id);
      return antwoordenInGroep.length;
    }
    return 0;
  }

  resultaatGroep(antw: Antwoord): Uploadfile[] {
    return this.resultaat.uploadedFiles.filter(uf => uf.groepId === antw.groep_id);
  }

  // controle toevoegen of limiet bereikt is. Limiet als (maxTotalSizeInMb - this.maxSizeInMb) < this.maxSizeInMb * 1024 * 1024
  uploadFile(file: File, antw: Antwoord): void {
    const maxFiles = this.geefMaxFiles(antw.id);

    if ((this.resultaatGroep(antw).length + this.numberOfUploadingFiles) < maxFiles) {
      if (FileValidator.isValidExtensionOfFile(antw, file)) {
        // Een document kan niet verkleind worden, dus als deze toegevoegd wordt mag de grootte niet groter worden dan het maximum
        if (!ImageHelper.isFileImage(file.name) && (this.totalSizeInBytes + (file.size * 4 / 3)) > (this.maxTotalSizeInMb * 1024 * 1024)) {
          this.addErrorMessage(this.errorTeGroot(file.name), antw);
        } else {
          // Het kan zijn dat een document toegevoegd is en daardoor te weinig ruimte overblijft voor een extra image
          if (ImageHelper.isFileImage(file.name) &&
            ((this.totalSizeInBytes + (this.maxSizeInMb * 1024 * 1024)) > (this.maxTotalSizeInMb * 1024 * 1024))) {
            this.addErrorMessage(this.errorTeGroot(file.name), antw);
          } else {
            this.readFile(file, antw);
          }
        }
      } else {
        // tslint:disable-next-line:max-line-length
        this.addErrorMessage(`Je kunt het bestand ${file.name} niet toevoegen. We ondersteunen dit type bestand niet. De volgende types kun je wel toevoegen: ${FileValidator.allowedExtensions(this.vraag).join(', ')}.`, antw);
      }
    } else {
      this.addErrorMessage(`Je kunt maximaal ${maxFiles} bestanden toevoegen.`, antw);
    }
  }

  errorTeGroot(filename: string): string {
    // tslint:disable-next-line:max-line-length
    const melding = 'Je kunt het bestand ' + filename + ' niet toevoegen, het bestand is te groot. Je kunt het bestand wel e-mailen, dit kan naar schadeafhandeling@interpolis.nl.';
    if (this._dataService.getProces() === Proces.SchademeldenZakelijk || this._dataService.getProces() === Proces.SchademeldenZakelijkAnoniem) {
      return melding;
    }
    if (this._dataService.getProces() === Proces.Schademelden) {
      return melding + ' Zet in het onderwerp het schadenummer dat je na afloop van je online schademelding krijgt.';
    } else {
      return melding + ' Zet in het onderwerp het schadenummer dat je hierboven ziet staan. Dit nummer begint met QS.';
    }
  }

  readFile(file: File, antw: Antwoord): void {
    this.numberOfUploadingFiles++;

    if (ImageHelper.isFileImage(file.name) && !this.hasCorrectFileSize(file)) {
      // Image is too large --> resize
      this.resizeFileWithOrientation(file)
        .pipe(
          mergeMap(resizedFile => this.readFileWithFileReader(resizedFile)),
          takeUntil(this.unsubscribe$))
        .subscribe(dataUrl => this.addFileToResultaat(dataUrl, file.name, file.type, antw),
          (e) => this.handleErrorImage(file.name, e, antw));
    } else {
      this.readFileWithFileReader(file)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(dataUrl => this.addFileToResultaat(dataUrl, file.name, file.type, antw),
          (e) => this.handleErrorImage(file.name, e, antw));
    }
  }

  addFileToResultaat(url: string, filename: string, fileType: string, antw: Antwoord) {
    const antw_id = this.bepaalAntwoordId(antw);
    this.zone.run(() => {
      this.numberOfUploadingFiles--;
      if ((this.totalSizeInBytes + url.length) <= (this.maxTotalSizeInMb * 1024 * 1024)) {
        this.resultaat.uploadedFiles.push(new Uploadfile(filename, fileType, url, antw.groep_id, antw_id));
        this.totalSizeInBytes = this.totalSizeInBytes + url.length;
        this.notifyChange();
        this._analyticsService.celebrusReinit();
      } else {
        this.addErrorMessage(this.errorTeGroot(filename), antw);
      }
    });
  }

  bepaalAntwoordId(antw: Antwoord): string {
    // Bepaal 1e niet gebruikte AntwoordId binnen de groep
    const files = this.resultaat.uploadedFiles.filter(uf => uf.groepId === antw.groep_id);

    if (!files || files.length === 0) {
      return antw.id;
    }

    let gevondenId = '';

    for (const antwoord of this.vraag.antwoorden.filter(a => a.groep_id === antw.groep_id)) {
      if (gevondenId.length === 0 && files.findIndex(f => f.antwoordId === antwoord.id) < 0) {
        gevondenId = antwoord.id;
        break;
      }
    }
    return gevondenId;
  }

  resizeFileWithOrientation(file: File): Observable<File> {
    if (!file) {
      return EMPTY;
    }

    const type = file.type;
    const name = file.name;
    return this._imageService.getImageFromFile(file)
      .pipe(
        mergeMap(img => this._imageService.getOrientedImage(img)),
        mergeMap(orientedImg => this.resizeImage(orientedImg, type, name)));
  }

  resizeFile(file: File): Observable<File> {
    if (!file) {
      return EMPTY;
    }

    const type = file.type;
    const name = file.name;

    return this._imageService.getImageFromFile(file)
      .pipe(mergeMap(img => this.resizeImage(img, type, name)));
  }

  resizeImage(image: HTMLImageElement, type: string, name: string): Observable<File> {
    if (!image) {
      return EMPTY;
    }

    return this._imageService.resizeImage(image, image.width / 2, image.height / 2, name, type)
      .pipe(mergeMap((file: File) => {
        if (!this.hasCorrectFileSize(file)) {
          return this.resizeFile(file);
        }
        return of(file);
      }));
  }

  hasCorrectFileSize(file: File): boolean {
    if (file == null) {
      return false;
    }

    // rekening houden met base64
    return file.size <= (this.maxSizeInMb * 1024 * 1024 * 3 / 4);
  }

  hasCorrectBase64Length(stringValue: string): boolean {
    if (!stringValue) {
      return true;
    }

    return stringValue.length <= (this.maxSizeInMb * 1024 * 1024);
  }

  handleErrorImage(filename: string, error: any, antw: Antwoord) {
    this.numberOfUploadingFiles--;
    this.notifyChange();
    if (filename) {
      this.addErrorMessage(`Er is een onverwachte fout opgetreden bij het uploaden van het bestand ${filename}.`, antw);
    }
    this._analyticsService.googleEmitEvent('error', 'client', 'ols-photo-upload');
  }

  blobToFile(blob: Blob, name: string): File {
    const anyFile: any = blob;
    anyFile.name = name;
    anyFile.lastModified = new Date().getTime();
    return anyFile;
  }

  readFileWithFileReader(file: File): Observable<string> {
    if (!file) {
      return EMPTY;
    }

    return this._imageService.getDataUrlFromFile(file)
      .pipe(mergeMap(dataUrl => {
        if (ImageHelper.isFileImage(file.name) && !this.hasCorrectBase64Length(dataUrl)) {
          return this.resizeFileWithOrientation(file)
            .pipe(mergeMap(resizedFile => this.readFileWithFileReader(resizedFile)));
        } else {
          return of(dataUrl);
        }
      }));
  }

  isValidFile(file: File, antw: Antwoord): boolean {
    if (file == null) {
      return false;
    }

    return FileValidator.isValidExtensionOfFile(antw, file) &&
      ((this.resultaatGroep(antw).length + this.numberOfUploadingFiles) < this.geefMaxFiles(antw.id));
  }

  clearUploadedFile() {
    this.newFiles.forEach(element => {
      element.nativeElement.value = '';
    });
  }

  deleteFile(file: Uploadfile, antw: Antwoord): void {
    if (this.resultaat == null) {
      return;
    }

    this.antwoordFileToDelete = antw;
    this.indexFileToDelete = this.resultaat.uploadedFiles?.indexOf(file);
    this.deleteFilename = file.name;
    this.openModalDelete();
    const path = '/' + MetenMapper.formatGA(this._verzekering.sleutel)
      + '/' + MetenMapper.geefVolgnr(this.vraag, this._meetgegevens.reeks) + '-' + MetenMapper.formatGA(this.vraag.titel) + '-verwijderen';

    this._analyticsService.googleEmitPageview(path);
    this._analyticsService.celebrusReinit();
  }

  annuleerVerwijderen() {
    this.indexFileToDelete = -1;
    this.closeModalDelete(false);
  }

  isMobileView(): boolean {
    return this._windowService.deviceType() <= Devicetype.TabletPortrait;
  }

  verwijderen() {
    this.resultaat.uploadedFiles.splice(this.indexFileToDelete, 1);
    // bepaal opnieuw
    this.totalSizeInBytes = 0;
    for (const uploadedfile of this.resultaat.uploadedFiles) {
      this.totalSizeInBytes = this.totalSizeInBytes + (uploadedfile.blob.length * 3 / 4);
    }
    this.notifyChange();
    this.indexFileToDelete = -1;
    this.resetError(this.antwoordFileToDelete);
    this.closeModalDelete(true);
  }

  notifyChange() {
    const antwoorden: Antwoord[] = [];
    const vraagboomUploadResultaat: VraagboomUploadResultaat = new VraagboomUploadResultaat();
    let antw: Antwoord;
    let files: Uploadfile[];

    if (this.vraag && this.vraag.antwoorden) {
      for (const id of this.selectedIds) {
        // groep bepalen van antwoord en bijbehorende files toevoegen
        antw = this.vraag.antwoorden.find(a => a.id === id);

        if (antw) {
          files = this.resultaat.uploadedFiles.filter(u => u.groepId === antw.groep_id);

          if (files && files.length > 0) {
            antwoorden.push(antw);
            vraagboomUploadResultaat.uploadedFiles = vraagboomUploadResultaat.uploadedFiles.concat(files);
          }
        }
      }
    }

    this.setAntwoord(antwoorden, vraagboomUploadResultaat);
  }

  openModalDelete(): void {
    this.renderer.addClass(document.body, 'ols-modal-open-del');
    this.renderer.addClass(document.body.parentElement, 'ols-modal-open-del');
  }

  closeModalDelete(verwijderen: boolean): void {
    this.renderer.removeClass(document.body, 'ols-modal-open-del');
    this.renderer.removeClass(document.body.parentElement, 'ols-modal-open-del');
  }

  validate(vraag: Vraag, antwoorden: Antwoord[], resultaat: VraagboomUploadResultaat): boolean {
    const geselecteerdeGroepen: string[] = antwoorden.map(a => a.groep_id);

    if (!antwoorden
      || this.minChoices > geselecteerdeGroepen.length
      || (this.maxChoices > 0 && geselecteerdeGroepen.length > this.maxChoices)
      || !FileValidator.isValidNumberOfFilesAntwoord(antwoorden, resultaat)) {
      return false;
    }
    return true;
  }

  public fileOver(event) {
    this.isHovered = true;
  }

  public fileLeave(event) {
    this.isHovered = false;
  }

  plusSleutel(): string {
    if (this._verzekering != null && this._verzekering.sleutel != null && this._verzekering.sleutel) {
      return MetenMapper.format(this._verzekering.sleutel) + '-';
    }
    return '';
  }

  sleutelGroepidEnTitel(): string {
    return this.plusSleutel() + MetenMapper.geefVolgnr(this.vraag, this._meetgegevens.reeks) + '_' + MetenMapper.format(this.vraag.titel);
  }

  geefMeetid(id: string): string {
    return 'ipoclick-btn-' + this._dataService.getMeetidPazaProces() + '-indienen-'
      + this.sleutelGroepidEnTitel() + '_' + id;
  }

  geefMeetidMulti(vraagtitel: string, groep_titel: string): string {
    if (this._meetgegevens == null) {
      return '';
    }

    return 'ipoclick-checkbox-' + this._dataService.getMeetidPazaProces() + '-' +
      this._meetgegevens.status + this._meetgegevens.sleutel +
      MetenMapper.geefVolgnr(this.vraag, this._meetgegevens.reeks) + '_' +
      MetenMapper.format(this._verzekering.sleutel) + '-' +
      MetenMapper.format(vraagtitel) + '-' + MetenMapper.format(groep_titel);
  }

  geefMeetidIndex(id: string, index: string): string {
    const index1Based = index + 1;
    return 'ipoclick-btn-' + this._dataService.getMeetidPazaProces() + '-indienen-'
      + this.sleutelGroepidEnTitel() + '_' + id + '-' + index1Based;
  }

  geefMeetidFile(groep_titel: string): string {
    return 'ipoclick-upload_area-' + this._dataService.getMeetidPazaProces() + '-indienen-'
      + this.sleutelGroepidEnTitel() + '-' + MetenMapper.format(groep_titel);
  }
}
